// TODO: Add ellipsis property for long messages

import type { TExample } from '@atlas/uicorn/preview/modules';
import { useContext } from 'react';
import * as React from 'react';
import styled, { css } from 'styled-components';
import ChevronIcon from '@atlas/uicorn/lib/Icon/ChevronIcon';
import MoonIcon from '@atlas/uicorn/lib/Icon/MoonIcon';
import Option, { type TOptionProps } from '@atlas/uicorn/lib/Control/Option';
import { FormContext } from '@atlas/uicorn/lib/Control/Form';

const Button = React.memo(
    React.forwardRef<TButtonRef, TButtonProps>(function Button(props, ref): React.ReactElement {
        const {
            type,
            submit,
            danger,
            noHoverStyle,
            noFocusStyle,
            flex = 'center',
            disabled: propsDisabled,
            ...optionProps
        } = props;

        const { disabled: contextDisabled } = useContext(FormContext);

        const disabled = propsDisabled ?? contextDisabled;

        return (
            <SButton
                {...(optionProps as TOptionProps)}
                disabled={disabled}
                flex={flex}
                type={submit ? 'submit' : 'button'}
                $style={type ?? 'primary'}
                $danger={danger ?? false}
                // TODO: handle noHoverStyle & noFocusStyle for not plain buttons
                noHoverStyle={noHoverStyle || type !== 'plain'}
                noFocusStyle={noFocusStyle}
                ref={ref}
            />
        );
    }),
);

export default Button;

// #region Styles

const SButton = styled(Option)<{
    $style: TButtonType;
    $danger: boolean;
}>`
    font: ${(p) => p.theme.font.input};
    line-height: 2.2rem;

    ${(p) =>
        p.$style === 'plain'
            ? css`
                  ${p.noFocusStyle
                      ? ''
                      : css`
                            &:focus-visible {
                                box-shadow: 0 0 0 0.1rem ${p.$danger ? p.theme.color.red6 : p.theme.color.primary};
                            }
                        `}
              `
            : css`
                  font-weight: 550;
                  border-radius: ${(p) => p.theme.radius.medium};
                  min-height: 3.8rem;
                  min-width: 2.2rem;
                  padding: ${p.$style === 'tertiary' ? '0.7rem 0.4rem' : '0.7rem 1.1rem'};

                  &:where(:not(:disabled)):hover {
                      scale: 1.01;
                  }

                  &:not(:disabled):active {
                      scale: 0.98;
                      box-shadow: ${p.theme.shadow.no};
                      outline-offset: ${p.$style === 'tertiary' ? '-0.1rem' : '0.1rem'};
                  }

                  ${p.noFocusStyle
                      ? ''
                      : css`
                            &:focus-visible {
                                outline-color: ${p.$danger ? p.theme.color.red6 : p.theme.color.primary};
                                outline-offset: ${p.$style == 'tertiary' ? '0rem' : '0.2rem'};
                            }
                        `}

                  outline: 0.2rem solid transparent;
                  outline-offset: ${p.$style === 'tertiary' ? '-0.2rem' : '-0.1rem'};
              `}

    ${(p) =>
        p.$style === 'primary'
            ? css`
                  box-shadow: ${p.theme.shadow.extraLow};
                  will-change: scale, filter;

                  &,
                  &:not(:disabled):not([disabled]):hover,
                  &:disabled {
                      color: ${p.theme.color.primaryButtonText};
                  }

                  ${p.noFocusStyle
                      ? ''
                      : css`
                            &:focus-visible,
                            &:focus-visible:not(:disabled):not([disabled]):hover {
                                box-shadow: 0 0 0 0.1rem ${p.theme.color.primary};
                            }
                        `}

                  ${p.$danger
                      ? css`
                            background-color: ${p.theme.color.red6};

                            &:not(:disabled):active {
                                background-color: ${p.theme.color.red7};
                                box-shadow: ${p.theme.shadow.no};
                            }
                        `
                      : css`
                            background-image: linear-gradient(
                                135deg,
                                ${p.theme.color.gradientPrimaryStart} 0%,
                                ${p.theme.color.gradientPrimaryEnd} 100%
                            );

                            &:where(:not(:disabled)):hover {
                                filter: hue-rotate(12deg);
                            }

                            &:not(:disabled):active {
                                filter: hue-rotate(0);
                            }
                        `}

                  &:where(:not(:disabled)):hover {
                      box-shadow: ${p.theme.shadow.low};
                  }
              `
            : p.$style === 'secondary'
            ? css`
                  background-color: ${p.theme.color.grey0};
                  box-shadow: ${p.theme.shadow.secondaryAction};

                  &,
                  &:not(:disabled):not([disabled]):hover,
                  &:disabled {
                      color: ${p.$danger ? p.theme.color.red6 : p.theme.color.grey9};
                  }

                  &:not(:disabled):active {
                      box-shadow: ${p.theme.shadow.secondaryActionActive};
                  }
              `
            : p.$style === 'tertiary'
            ? css`
                  background-color: ${p.theme.color.grey0};

                  &,
                  &:not(:disabled):not([disabled]):hover,
                  &:disabled {
                    color: ${p.$danger ? p.theme.color.red6 : p.theme.color.primary};
                  }

                  &:where(:not(:disabled)):hover {
                      background-color: ${p.theme.color.grey5};
                  }

                  &:not(:disabled):active {
                    background-color: ${p.theme.color.grey6}};
                  }
              `
            : p.$style === 'plain'
            ? css`
                  ${p.$danger
                      ? css`
                            &,
                            &:not(:disabled):not([disabled]):hover,
                            &:disabled {
                                color: ${p.theme.color.red6};
                            }
                            outline-color: ${p.theme.color.red6};
                        `
                      : ''}
              `
            : ''}
`;

// #endregion

// #region Types

export type TButtonType = 'primary' | 'secondary' | 'tertiary' | 'plain';

export type TButtonFlex = 'center' | 'start' | 'end';

export type TButtonProps = Omit<TOptionProps, 'type'> & {
    type?: TButtonType;
    submit?: boolean;
    danger?: boolean;
};

export type TButtonRef = HTMLButtonElement;

// #endregion

// #region Examples

export const Example: TExample = {
    render: ({ width, withTooltip, link, ...props }) => {
        const style = width > 0 ? { width: `${width / 10}rem` } : undefined;

        const tooltip = withTooltip
            ? () => (
                  <span>
                      Refresh page <kbd>⌘R</kbd>
                  </span>
              )
            : undefined;

        return (
            <>
                <Button type="primary" style={style} tooltip={tooltip} href={link ? '#' : undefined} {...props} />
                <Button type="secondary" style={style} tooltip={tooltip} href={link ? '#' : undefined} {...props} />
                <Button type="tertiary" style={style} tooltip={tooltip} href={link ? '#' : undefined} {...props} />
                <Button type="plain" style={style} tooltip={tooltip} href={link ? '#' : undefined} {...props} />
            </>
        );
    },
    figma: 'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=4-1064&t=N252bJVpMS7uwjzX-0',
    controls: {
        children: { type: 'string', initial: 'Press me' },
        disabled: 'boolean',
        danger: 'boolean',
        loading: ['none', 'prefix', 'suffix'],
        width: { type: 'number', initial: 200, connected: false },
        prefix: { type: 'string', initial: '🦄', connected: false },
        pushPrefix: 'boolean',
        suffix: { type: 'string', initial: '→', connected: false },
        pushSuffix: 'boolean',
        flex: ['center', 'start', 'end'],
        withTooltip: 'boolean',
        link: 'boolean',
    },
    handlers: {
        onClick: 'console',
    },
};

export const AllVariantsExample: TExample = {
    layout: 'column',
    render(props) {
        const groupStyle: React.CSSProperties = { display: 'flex', gap: '0.5rem' };
        const wideStyle: React.CSSProperties = { width: '35rem' };
        return (
            <>
                <Button {...props}>Basic</Button>
                <Button {...props} href="#">
                    Basic link
                </Button>
                <Button {...props}>
                    Rich <strong>content</strong>
                </Button>
                <Button {...props} disabled>
                    Disabled
                </Button>
                <Button {...props} loading="prefix">
                    Loading=prefix
                </Button>
                <Button {...props} loading="prefix" disabled={false}>
                    Loading=prefix & not disabled
                </Button>
                <Button {...props} prefix={<MoonIcon />}>
                    Prefixed
                </Button>
                <Button {...props} suffix={<ChevronIcon />}>
                    Suffixed
                </Button>
                <Button {...props} prefix="🦄" suffix="→">
                    Prefixed & suffixed with strings
                </Button>
                <Button {...props} loading="prefix" prefix={<MoonIcon />} suffix={<ChevronIcon />}>
                    Prefixed & suffixed & loading=prefix
                </Button>
                <Button {...props} loading="suffix" prefix={<MoonIcon />} suffix={<ChevronIcon />}>
                    Prefixed & suffixed & loading=suffix
                </Button>
                <div style={groupStyle}>
                    <Button {...props} loading="prefix" />
                    <Button {...props} prefix={<MoonIcon />} />
                    <Button {...props} loading="prefix" suffix={<ChevronIcon />} />
                </div>
                <Button {...props} style={wideStyle} prefix={<MoonIcon />} suffix={<ChevronIcon />}>
                    Long text that wraps when there is not enough space
                </Button>
                <Button {...props} style={wideStyle} ellipsis>
                    Button with the long enough text that will be cut with ellipsis
                </Button>
                <Button {...props} style={wideStyle} prefix={<MoonIcon />} suffix={<ChevronIcon />}>
                    Prefixed & suffixed
                </Button>
                <Button {...props} style={wideStyle} danger prefix={<MoonIcon />} suffix={<ChevronIcon />}>
                    Danger prefixed & suffixed
                </Button>
                <Button {...props} style={wideStyle} prefix={<MoonIcon />} flex="start">
                    Wide & prefixed & flex start
                </Button>
                <Button {...props} style={wideStyle} suffix={<ChevronIcon />} flex="end">
                    Wide & suffixed & flex end
                </Button>
                <Button {...props} style={wideStyle} prefix={<MoonIcon />} pushPrefix suffix={<ChevronIcon />}>
                    Wide & pushed prefix & suffix
                </Button>
                <Button {...props} style={wideStyle} prefix={<MoonIcon />} suffix={<ChevronIcon />} pushSuffix>
                    Wide & prefix & pushed suffix
                </Button>
                <Button {...props} style={wideStyle} prefix={<MoonIcon />} suffix={<ChevronIcon />} pushSuffix href="#">
                    Wide link & prefix & pushed suffix
                </Button>
                <Button
                    {...props}
                    style={wideStyle}
                    suffix={
                        <>
                            <MoonIcon />
                            <ChevronIcon />
                        </>
                    }
                    pushSuffix
                >
                    Two actions on the right
                </Button>
            </>
        );
    },
    controls: {
        type: ['primary', 'secondary', 'tertiary', 'plain'],
    },
    handlers: {
        onClick: 'console',
    },
    figma: 'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0 disabled-%E2%80%93-Web?type=design&node-id=4-1064&t=N252bJVpMS7uwjzX-0',
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('Button', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        it('matches snapshot', () => {
            const result = render(<Button>Press</Button>);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
